import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { Mail, Phone, Calendar, X, User } from 'lucide-react'
import { useTopLayerPortal } from '../utils/top-layer-portal'

interface SchedulingContactPopupProps {
  primaryColor: string
  customerName?: string // Name the bot already collected (used for header copy)
  /** Optional name to prefill the name input (e.g. captured by the bot during the call) */
  defaultName?: string
  onSubmit: (email: string, phone: string, name?: string) => void
  onDismiss: () => void
  /** 'calendly' (default) = pre-Calendly flow, 'callback' = no-Calendly callback flow */
  mode?: 'calendly' | 'callback'
  /** Future: OTP verification step. When implemented, onSubmit will be called
   *  only after phone is verified. The component is structured to support
   *  inserting a verification step between phone input and submission. */
  otpEnabled?: boolean // Placeholder — not implemented yet
}

/**
 * SchedulingContactPopup — Minimal pre-Calendly contact popup.
 * Shown in deferred mode when bot calls book_meeting and user has no contact info.
 * Only asks email + phone with scheduling-relevant context.
 * OTP-ready: the flow is structured so a verification step can be inserted
 * between phone entry and final submission without changing the parent interface.
 */
export const SchedulingContactPopup: React.FC<SchedulingContactPopupProps> = ({
  primaryColor,
  customerName,
  defaultName,
  onSubmit,
  onDismiss,
  mode = 'calendly',
  // Future: otpEnabled will gate phone verification flow
  otpEnabled: _otpEnabled = false,
}) => {
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [name, setName] = useState(() => (defaultName || customerName || '').trim())
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [nameError, setNameError] = useState('')
  const portalTarget = useTopLayerPortal()
  // Future OTP state placeholder:
  // const [otpStep, setOtpStep] = useState(false)
  // const [otpCode, setOtpCode] = useState('')

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneRegex = /^[0-9]{9,15}$/

  const validateAndSubmit = () => {
    let hasError = false

    // Name validation (required in both modes — used to prefill Calendly so it doesn't fall back to "אורח")
    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError('נא להזין שם מלא')
      hasError = true
    } else {
      setNameError('')
    }

    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setEmailError('נא להזין כתובת אימייל')
      hasError = true
    } else if (!emailRegex.test(trimmedEmail)) {
      setEmailError('כתובת אימייל לא תקינה')
      hasError = true
    } else {
      setEmailError('')
    }

    const cleanPhone = phone.replace(/[-\s()]/g, '')
    if (!cleanPhone) {
      setPhoneError('נא להזין מספר טלפון')
      hasError = true
    } else if (!phoneRegex.test(cleanPhone)) {
      setPhoneError('מספר טלפון לא תקין (9-15 ספרות)')
      hasError = true
    } else {
      setPhoneError('')
    }

    if (hasError) return

    // Future OTP: if otpEnabled, show OTP step here before calling onSubmit
    // if (otpEnabled && !otpStep) { sendOtp(cleanPhone); setOtpStep(true); return }
    // if (otpEnabled && otpStep) { verifyOtp(otpCode).then(() => onSubmit(...)) }

    onSubmit(trimmedEmail, cleanPhone, name.trim() || undefined)
  }

  const adjustBrightness = (hex: string, amount: number): string => {
    const num = parseInt(hex.replace('#', ''), 16)
    const r = Math.min(255, Math.max(0, (num >> 16) + amount))
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount))
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount))
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`
  }

  // Render the overlay into the persistent top-layer portal so it sits
  // above the panel (and any host-page stacking context). If the portal
  // target is somehow unavailable, fall back to rendering inline rather
  // than returning null — a contact form must NEVER silently fail to show.
  const overlay = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1,
        padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
        boxSizing: 'border-box',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        pointerEvents: 'auto',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 360,
          maxHeight: 'min(560px, calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
          direction: 'rtl',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="סגירת הטופס"
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: 'none',
            background: '#f3f4f6',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>

        {/* Header icon */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: `${primaryColor}20`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {mode === 'callback'
              ? <Phone size={24} color={primaryColor} />
              : <Calendar size={24} color={primaryColor} />}
          </div>
        </div>

        {/* Title */}
        <h3 style={{
          textAlign: 'center',
          fontSize: 18,
          fontWeight: 700,
          color: '#1a1a1a',
          margin: '0 0 6px 0',
        }}>
          {mode === 'callback'
            ? 'השאר/י פרטים ונחזור אליך'
            : (customerName ? `${customerName}, רגע לפני שנפתח את היומן` : 'רגע לפני שנפתח את היומן')}
        </h3>

        {/* Subtitle */}
        <p style={{
          textAlign: 'center',
          fontSize: 13,
          color: '#666',
          margin: '0 0 20px 0',
          lineHeight: 1.5,
        }}>
          {mode === 'callback'
            ? 'נא להזין שם, אימייל וטלפון כדי שנציג שלנו יוכל ליצור איתך קשר'
            : 'נא להזין שם, אימייל וטלפון כדי שנוכל לשלוח לך אישור ותזכורת לפגישה'}
        </p>

        {/* Name field (shown in both modes — Calendly prefill needs it) */}
        {(
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="scheduling-name" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: '#444',
              marginBottom: 6,
              justifyContent: 'flex-end',
            }}>
              שם מלא
              <User size={14} color="#666" />
            </label>
            <input
              id="scheduling-name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError('') }}
              placeholder="ישראל ישראלי"
              dir="rtl"
              autoComplete="name"
              enterKeyHint="next"
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 10,
                border: nameError ? '1.5px solid #ef4444' : '1.5px solid #e0e0e0',
                // 16px minimum: anything smaller makes iOS Safari zoom the
                // page in when the field is focused.
                fontSize: 16,
                outline: 'none',
                direction: 'rtl',
                textAlign: 'right',
                backgroundColor: '#ffffff',
                color: '#111827',
                WebkitTextFillColor: '#111827',
                caretColor: '#111827',
                appearance: 'none',
                WebkitAppearance: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => !nameError && (e.target.style.borderColor = primaryColor)}
              onBlur={(e) => !nameError && (e.target.style.borderColor = '#e0e0e0')}
            />
            {nameError && (
              <p style={{ color: '#ef4444', fontSize: 11, margin: '4px 0 0 0', textAlign: 'right' }}>
                {nameError}
              </p>
            )}
          </div>
        )}

        {/* Email field */}
        <div style={{ marginBottom: 14 }}>
          <label htmlFor="scheduling-email" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: '#444',
            marginBottom: 6,
            justifyContent: 'flex-end',
          }}>
            אימייל
            <Mail size={14} color="#666" />
          </label>
          <input
            id="scheduling-email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError('') }}
            placeholder="example@email.com"
            dir="ltr"
            autoComplete="email"
            inputMode="email"
            enterKeyHint="next"
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 10,
              border: emailError ? '1.5px solid #ef4444' : '1.5px solid #e0e0e0',
              fontSize: 16,
              outline: 'none',
              direction: 'ltr',
              textAlign: 'left',
              backgroundColor: '#ffffff',
              color: '#111827',
              WebkitTextFillColor: '#111827',
              caretColor: '#111827',
              appearance: 'none',
              WebkitAppearance: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => !emailError && (e.target.style.borderColor = primaryColor)}
            onBlur={(e) => !emailError && (e.target.style.borderColor = '#e0e0e0')}
          />
          {emailError && (
            <p style={{ color: '#ef4444', fontSize: 11, margin: '4px 0 0 0', textAlign: 'right' }}>
              {emailError}
            </p>
          )}
        </div>

        {/* Phone field */}
        <div style={{ marginBottom: 18 }}>
          <label htmlFor="scheduling-phone" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: '#444',
            marginBottom: 6,
            justifyContent: 'flex-end',
          }}>
            טלפון
            <Phone size={14} color="#666" />
          </label>
          <input
            id="scheduling-phone"
            type="tel"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setPhoneError('') }}
            placeholder="050-1234567"
            dir="ltr"
            autoComplete="tel"
            inputMode="tel"
            enterKeyHint="done"
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 10,
              border: phoneError ? '1.5px solid #ef4444' : '1.5px solid #e0e0e0',
              fontSize: 16,
              outline: 'none',
              direction: 'ltr',
              textAlign: 'left',
              backgroundColor: '#ffffff',
              color: '#111827',
              WebkitTextFillColor: '#111827',
              caretColor: '#111827',
              appearance: 'none',
              WebkitAppearance: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => !phoneError && (e.target.style.borderColor = primaryColor)}
            onBlur={(e) => !phoneError && (e.target.style.borderColor = '#e0e0e0')}
          />
          {phoneError && (
            <p style={{ color: '#ef4444', fontSize: 11, margin: '4px 0 0 0', textAlign: 'right' }}>
              {phoneError}
            </p>
          )}
        </div>

        {/* Submit button */}
        <button
          type="button"
          onClick={validateAndSubmit}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: 12,
            border: 'none',
            backgroundColor: primaryColor,
            color: '#fff',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = adjustBrightness(primaryColor, -20))}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = primaryColor)}
        >
          {mode === 'callback' ? 'שליחת פרטים' : 'אישור ופתיחת היומן'}
        </button>

        {/* Privacy footer */}
        <p style={{
          textAlign: 'center',
          fontSize: 11,
          color: '#999',
          margin: '12px 0 0 0',
          lineHeight: 1.4,
        }}>
          הפרטים שלך ישמרו באופן מאובטח ולא ישותפו עם צד שלישי
        </p>
      </motion.div>
    </motion.div>
  )

  return portalTarget ? createPortal(overlay, portalTarget) : overlay
}
